import {
  ONLINE_QUERY_COUNT_BY_NAME_CONFIG,
  ONLINE_QUERY_COUNT_BY_RESOURCE_GROUP_CONFIG,
} from "@/components/query/QueryShared";
import { listBranchWithLatestDeployments } from "@/gen_src/proto/chalk/server/v1/branches-BranchService_connectquery";
import { getChartSnapshot } from "@/gen_src/proto/chalk/server/v1/chart-ChartsService_connectquery";
import { listCloudCredentials } from "@/gen_src/proto/chalk/server/v1/cloud_credentials-CloudAccountCredentialsService_connectquery";
import {
  listCloudComponentCluster,
  listCloudComponentContainerRegistry,
  listCloudComponentStorage,
  listCloudComponentVpc,
} from "@/gen_src/proto/chalk/server/v1/cloud_components-CloudComponentsService_connectquery";
import { listDatasets } from "@/gen_src/proto/chalk/server/v1/datasets-DatasetMetadataService_connectquery";
import { listDeployments } from "@/gen_src/proto/chalk/server/v1/deploy-DeployService_connectquery";
import { getFeatureFlags } from "@/gen_src/proto/chalk/server/v1/flag-FeatureFlagService_connectquery";
import { getGraph } from "@/gen_src/proto/chalk/server/v1/graph-GraphService_connectquery";
import {
  getProjectNavbarCounts,
  getTeamNavbarCounts,
} from "@/gen_src/proto/chalk/server/v1/inventory-InventoryService_connectquery";
import { listIntegrations } from "@/gen_src/proto/chalk/server/v1/integrations-IntegrationsService_connectquery";
import { getKubernetesServiceAccounts } from "@/gen_src/proto/chalk/server/v1/kube-KubeService_connectquery";
import { listIncidents } from "@/gen_src/proto/chalk/server/v1/monitoring-MonitoringService_connectquery";
import { listOfflineQueries } from "@/gen_src/proto/chalk/server/v1/offline_queries-OfflineQueryMetadataService_connectquery";
import {
  listLatestMetaQueries,
  listMetaQueryRuns,
} from "@/gen_src/proto/chalk/server/v1/queries-QueriesService_connectquery";
import { getHealth } from "@/gen_src/proto/chalk/server/v1/status-HealthService_connectquery";
import {
  listServiceTokens,
  listServiceTokensTeamScoped,
  getTeam,
  getTeamPermissions,
} from "@/gen_src/proto/chalk/server/v1/team-TeamService_connectquery";
import {
  getSpanFacets,
  getTraceCallGraph,
  listSpan,
  listSpanAggregated,
  listTrace,
  searchTraceSummaries,
} from "@/gen_src/proto/chalk/server/v1/trace-TraceService_connectquery";
import { durationFromSeconds } from "@/utils/proto-utils";
import { DescMessage, DescMethodUnary, MessageInitShape } from "@bufbuild/protobuf";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";
import { Transport } from "@connectrpc/connect";

type DashboardFixtureDefaultCaptureInput<I extends DescMessage> =
  | MessageInitShape<I>
  | (() => MessageInitShape<I>);

interface DashboardFixtureDefaultCaptureStep<I extends DescMessage = DescMessage> {
  environmentScoped?: boolean;
  input?: DashboardFixtureDefaultCaptureInput<I>;
  method: DescMethodUnary<I>;
}

export interface DashboardFixtureDefaultCaptureResult {
  failed: number;
  skipped: number;
  succeeded: number;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const makeTimestampWindow = (windowMs: number) => {
  const end = new Date();
  return {
    endTime: timestampFromDate(end),
    startTime: timestampFromDate(new Date(end.getTime() - windowMs)),
  };
};

const makeOnlineQueryRange = () => {
  const end = new Date();
  return {
    end: timestampFromDate(end),
    start: timestampFromDate(new Date(end.getTime() - 30 * DAY_MS)),
  };
};

const makeOnlineQueryChartInput = (metricConfig: typeof ONLINE_QUERY_COUNT_BY_NAME_CONFIG) => {
  const { endTime, startTime } = makeTimestampWindow(30 * DAY_MS);
  return {
    endTime,
    metricConfig,
    startTime,
  };
};

const makeSpanListInput = () => ({
  ...makeTimestampWindow(HOUR_MS),
  limit: 100,
  pageToken: undefined as unknown as string,
});

const makeTraceListInput = () => ({
  ...makeTimestampWindow(HOUR_MS),
  limit: 20,
  pageToken: undefined as unknown as string,
});

const makeSearchTraceSummariesInput = () => ({
  ...makeTimestampWindow(HOUR_MS),
  limit: 100,
  pageToken: undefined as unknown as string,
});

const makeTraceCallGraphInput = () => ({
  ...makeTimestampWindow(HOUR_MS),
  attributeFilters: [],
  limit: 100,
  resourceAttributeFilters: [],
});

const makeSpanAggregatedInput = () => ({
  ...makeTimestampWindow(HOUR_MS),
  windowPeriod: durationFromSeconds(36),
});

const DEFAULT_CAPTURE_STEPS: DashboardFixtureDefaultCaptureStep[] = [
  { method: getTeam },
  { method: getTeamPermissions },
  { method: getTeamNavbarCounts },
  { method: getHealth },
  { method: listServiceTokens },
  { method: listServiceTokensTeamScoped },
  { method: listCloudCredentials },
  { method: listCloudComponentVpc },
  { method: listCloudComponentCluster },
  { method: listCloudComponentContainerRegistry },
  { method: listCloudComponentStorage },
  { method: getFeatureFlags, environmentScoped: true },
  { method: getProjectNavbarCounts, environmentScoped: true },
  { method: getGraph, environmentScoped: true },
  { method: listDeployments, environmentScoped: true },
  {
    method: listDeployments,
    environmentScoped: true,
    input: { cursor: "", includeBranch: true, limit: 10_000 },
  },
  {
    method: listBranchWithLatestDeployments,
    environmentScoped: true,
    input: { cursor: "", limit: 10_000 },
  },
  { method: listIntegrations, environmentScoped: true },
  { method: listLatestMetaQueries, environmentScoped: true },
  { method: listMetaQueryRuns, environmentScoped: true, input: makeOnlineQueryRange },
  {
    method: getChartSnapshot,
    environmentScoped: true,
    input: () => makeOnlineQueryChartInput(ONLINE_QUERY_COUNT_BY_NAME_CONFIG),
  },
  {
    method: getChartSnapshot,
    environmentScoped: true,
    input: () => makeOnlineQueryChartInput(ONLINE_QUERY_COUNT_BY_RESOURCE_GROUP_CONFIG),
  },
  { method: listOfflineQueries, environmentScoped: true },
  { method: listDatasets, environmentScoped: true },
  { method: listIncidents, environmentScoped: true },
  { method: getKubernetesServiceAccounts, environmentScoped: true },
  { method: getSpanFacets, environmentScoped: true },
  { method: listSpan, environmentScoped: true, input: makeSpanListInput },
  { method: listSpanAggregated, environmentScoped: true, input: makeSpanAggregatedInput },
  { method: listTrace, environmentScoped: true, input: makeTraceListInput },
  { method: searchTraceSummaries, environmentScoped: true, input: makeSearchTraceSummariesInput },
  { method: getTraceCallGraph, environmentScoped: true, input: makeTraceCallGraphInput },
];

function getStepInput<I extends DescMessage>(
  step: DashboardFixtureDefaultCaptureStep<I>,
): MessageInitShape<I> {
  if (typeof step.input === "function") {
    return step.input();
  }
  return step.input ?? ({} as MessageInitShape<I>);
}

export async function runDefaultDashboardFixtureCapture({
  hasEnvironment,
  transport,
}: {
  hasEnvironment: boolean;
  transport: Transport;
}): Promise<DashboardFixtureDefaultCaptureResult> {
  let succeeded = 0;
  let failed = 0;
  let skipped = 0;

  for (const step of DEFAULT_CAPTURE_STEPS) {
    if (step.environmentScoped && !hasEnvironment) {
      skipped += 1;
      continue;
    }

    try {
      await transport.unary(step.method, undefined, undefined, undefined, getStepInput(step));
      succeeded += 1;
    } catch {
      failed += 1;
    }
  }

  return { failed, skipped, succeeded };
}
